import { ApiResponse } from "../types/common";
import { UploadResult } from "../types/upload";
import { ApiClient } from "./apiClient";

const uploadService = new ApiClient<ApiResponse<string>>("/upload");

// Re-export for backward compatibility
export type { UploadResult };

export const uploadPaymentScreenshot = async (
  file: File,
  options?: { signal?: AbortSignal }
): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await uploadService.custom(
    "post",
    "/payment-screenshot",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000,
      signal: options?.signal,
    }
  );

  if (!response.isSuccess || !response.data) {
    throw new Error(response.message || "Failed to upload screenshot");
  }

  return response.data;
};

/**
 * Upload a product image
 * @param file The image file to upload
 * @param onProgress Optional progress callback (0-100)
 * @param options Optional configuration including AbortSignal for cancellation
 * @returns The URL of the uploaded image
 */
export const uploadProductImage = async (
  file: File,
  onProgress?: (progress: number) => void,
  options?: { signal?: AbortSignal }
): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await uploadService.custom(
    "post",
    "/product-image",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for large images
      signal: options?.signal,
      onUploadProgress: onProgress
        ? (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            onProgress(percentCompleted);
          }
        : undefined,
    }
  );

  if (!response.isSuccess || !response.data) {
    throw new Error(response.message || "Failed to upload image");
  }

  // Handle both string response and object response
  if (typeof response.data === "string") {
    return {
      url: response.data,
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
    };
  }

  return response.data as unknown as UploadResult;
};

/**
 * Validate image file before upload
 * @param file The file to validate
 * @param maxSizeMB Maximum file size in MB (default 5MB)
 * @returns Object with isValid and error message
 */
export const validateImageFile = (
  file: File,
  maxSizeMB = 5
): { isValid: boolean; error?: string } => {
  const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  if (!validTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed: ${validTypes.map((t) => t.split("/")[1].toUpperCase()).join(", ")}`,
    };
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`,
    };
  }

  return { isValid: true };
};

export default uploadService;
